import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import ReactDOM, { createPortal } from "react-dom";
import {
  FiSearch,
  FiChevronDown,
  FiCheck,
  FiRefreshCw,
  FiAlertCircle,
  FiX,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiCheckCircle,
  FiShield,
  FiClock,
  FiLoader,
} from "react-icons/fi";
import api from "../../../connector/api";
import Pagination from "../reuse/Pagination";
import UniformInfractionModal from "../modal/UniformInfractionModal";

// --- CUSTOM PORTAL DROPDOWN COMPONENT ---
function CustomDropdown({ label, value, options, onChange, icon: Icon }) {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef(null);

  const selectedOption =
    options.find((opt) => String(opt.value) === String(value)) || options[0];

  const updateCoords = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: Math.max(rect.width, 160),
      });
    }
  };

  const toggleDropdown = () => {
    if (!isOpen) updateCoords();
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleScrollOrResize = () => {
      if (isOpen) updateCoords();
    };
    const handleClickOutside = (e) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(e.target) &&
        !e.target.closest(".portal-dropdown-menu")
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [isOpen]);

  return (
    <div className="relative inline-block w-full">
      <button
        ref={buttonRef}
        type="button"
        onClick={toggleDropdown}
        className="w-full flex items-center justify-between gap-2 px-3 py-1.5 bg-zinc-50 border border-zinc-200/80 rounded-lg text-zinc-700 hover:bg-zinc-100/80 focus:outline-none transition-colors text-xs font-medium cursor-pointer"
      >
        <div className="flex items-center gap-2 truncate">
          {Icon && <Icon className="w-3.5 h-3.5 text-zinc-400 shrink-0" />}
          <span className="truncate text-zinc-700">{selectedOption?.label}</span>
        </div>
        <FiChevronDown
          className={`w-3.5 h-3.5 text-zinc-400 transition-transform duration-200 shrink-0 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen &&
        ReactDOM.createPortal(
          <div
            style={{
              top: `${coords.top}px`,
              left: `${coords.left}px`,
              minWidth: `${coords.width}px`,
            }}
            className="portal-dropdown-menu fixed bg-white border border-zinc-200 rounded-lg shadow-xl py-1 z-[9999] animate-in fade-in zoom-in-95 duration-100 max-h-56 overflow-y-auto"
          >
            {label && (
              <div className="px-3 py-1 border-b border-zinc-100 text-[10px] font-bold text-zinc-400 uppercase tracking-wider">
                {label}
              </div>
            )}
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center justify-between px-3 py-1.5 text-left transition-colors text-xs cursor-pointer ${
                  String(value) === String(option.value)
                    ? "bg-zinc-100 text-zinc-900 font-semibold"
                    : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
                }`}
              >
                <span className="truncate">{option.label}</span>
                {String(value) === String(option.value) && (
                  <FiCheck className="w-3.5 h-3.5 text-zinc-600 shrink-0" />
                )}
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}

// --- HELPER FUNCTION: GET WEEK NUMBER ---
function getWeekKey(dateObj) {
  if (!dateObj || isNaN(dateObj.getTime())) return null;
  const d = new Date(Date.UTC(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  return `${d.getUTCFullYear()}-W${weekNo}`;
}

export default function DressCodeTab() {
  // --- DATA STATES ---
  const [infractions, setInfractions] = useState([]);
  const [schoolYearTerms, setSchoolYearTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- MODALS & SELECTIONS ---
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedInfraction, setSelectedInfraction] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // --- FILTERS & PAGINATION ---
  const [search, setSearch] = useState("");
  const [selectedItem, setSelectedItem] = useState("ALL");
  const [selectedTerm, setSelectedTerm] = useState("ALL");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalInfractions, setTotalInfractions] = useState(0);

  // --- TOAST NOTIFICATIONS ---
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const showToast = useCallback((message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "success" });
    }, 3500);
  }, []);

  const VIOLATION_OPTIONS = [
    { value: "ALL", label: "All Violations" },
    { value: "No ID Card", label: "No ID Card" },
    { value: "No Tie", label: "No Tie" },
    { value: "No PE Uniform", label: "No PE Uniform" },
    { value: "Wrong Shoes", label: "Wrong Shoes" },
    { value: "Wrong Hair Color", label: "Wrong Hair Color" },
    { value: "No School Logo", label: "No School Logo" },
    { value: "Wrong Shirt Sleeves", label: "Wrong Shirt Sleeves" },
  ];

  // --- FETCH DATA ---
  const fetchInfractions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (selectedItem !== "ALL") params.append("specific_item", selectedItem);
      if (selectedTerm !== "ALL") params.append("school_year_term", selectedTerm);
      params.append("page", currentPage);
      params.append("page_size", pageSize);

      const response = await api.get(`/uniform-infraction/?${params.toString()}`);
      const data = response.data !== undefined ? response.data : await response.json();

      if (data && Array.isArray(data.results)) {
        setInfractions(data.results);
        setTotalInfractions(data.count || 0);
        setTotalPages(Math.ceil((data.count || 0) / pageSize) || 1);
      } else if (Array.isArray(data)) {
        setInfractions(data);
        setTotalInfractions(data.length);
        setTotalPages(1);
      } else {
        setInfractions([]);
        setTotalInfractions(0);
        setTotalPages(1);
      }
    } catch (err) {
      console.error("Error loading infractions:", err);
      setError(err.response?.data?.detail || err.message || "Failed to load dress code infractions.");
      setInfractions([]);
    } finally {
      setLoading(false);
    }
  }, [search, selectedItem, selectedTerm, currentPage, pageSize]);

  const fetchTerms = useCallback(async () => {
    try {
      const response = await api.get("/year-terms/");
      const data = response.data?.results || response.data;
      setSchoolYearTerms(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching terms:", err);
    }
  }, []);

  useEffect(() => {
    fetchInfractions();
    fetchTerms();
  }, [fetchInfractions, fetchTerms]);

  const termOptions = useMemo(() => {
    const base = [{ value: "ALL", label: "All Terms" }];
    const fetched = schoolYearTerms.map((t) => ({
      value: String(t.id),
      label: t.name || t.term?.name || `Term #${t.id}`,
    }));
    return [...base, ...fetched];
  }, [schoolYearTerms]);

  // --- CALCULATE WEEKLY INFRACTIONS MAP ---
  const weeklyInfractionsMap = useMemo(() => {
    const map = {};
    infractions.forEach((item) => {
      const studentId = item.student_uid || item.student || item.student_name;
      if (!studentId || !item.date_of_infraction) return;

      const dateObj = new Date(item.date_of_infraction);
      const weekKey = getWeekKey(dateObj);
      if (!weekKey) return;

      const key = `${studentId}_${weekKey}`;
      map[key] = (map[key] || 0) + 1;
    });
    return map;
  }, [infractions]);

  // Helper to get total count for a given item row
  const getWeeklyCount = (item) => {
    if (item.weekly_infractions_count !== undefined) {
      return item.weekly_infractions_count;
    }
    const studentId = item.student_uid || item.student || item.student_name;
    if (!studentId || !item.date_of_infraction) return "—";

    const dateObj = new Date(item.date_of_infraction);
    const weekKey = getWeekKey(dateObj);
    const key = `${studentId}_${weekKey}`;

    return weeklyInfractionsMap[key] || 1;
  };

  // --- ACTIONS ---
  const handleOpenFormModal = (item = null, e = null) => {
    if (e) e.stopPropagation();
    setSelectedInfraction(item);
    setIsFormModalOpen(true);
  };

  const handleOpenDeleteModal = (item, e) => {
    if (e) e.stopPropagation();
    setSelectedInfraction(item);
    setIsDeleteModalOpen(true);
  };

  const handleSaveInfraction = async (payload, id = null) => {
    try {
      let res;
      if (id) {
        res = await api.put(`/uniform-infraction/${id}/`, payload);
        showToast("Infraction record updated successfully!", "success");
      } else {
        res = await api.post("/uniform-infraction/", payload);
        showToast("Infraction record created successfully!", "success");
      }
      setIsFormModalOpen(false);
      fetchInfractions();
      return res.data;
    } catch (err) {
      console.error("Error saving infraction:", err);
      const errMsg = err.response?.data
        ? typeof err.response.data === "object"
          ? JSON.stringify(err.response.data)
          : err.response.data
        : "Failed to save infraction.";
      showToast(errMsg, "error");
      throw err;
    }
  };

  const handleDeleteInfraction = async () => {
    if (!selectedInfraction) return;
    setSubmitting(true);
    try {
      await api.delete(`/uniform-infraction/${selectedInfraction.id}/`);
      setIsDeleteModalOpen(false);
      setSelectedInfraction(null);
      showToast("Infraction record deleted.", "success");
      fetchInfractions();
    } catch (err) {
      console.error("Error deleting infraction:", err);
      showToast(err.response?.data?.detail || "Failed to delete infraction.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const renderModals = () => (
    <>
      <UniformInfractionModal
        isOpen={isFormModalOpen}
        onClose={() => setIsFormModalOpen(false)}
        onSave={handleSaveInfraction}
        infractionData={selectedInfraction}
      />

      {isDeleteModalOpen &&
        createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-zinc-900/40 backdrop-blur-xs">
            <div className="bg-white rounded-lg border border-zinc-200/80 shadow-2xl w-full max-w-sm p-5 space-y-4 text-center">
              <div className="w-10 h-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center mx-auto">
                <FiTrash2 className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-zinc-900">Delete Infraction Record</h3>
                <p className="text-xs text-zinc-500 mt-1">
                  Are you sure you want to delete the record for{" "}
                  <span className="font-bold text-zinc-800">
                    {selectedInfraction?.student_name || "this student"}
                  </span>
                  ? This action cannot be undone.
                </p>
              </div>
              <div className="flex items-center justify-center gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="w-full py-1.5 border border-zinc-200 text-zinc-600 hover:bg-zinc-50 rounded-lg text-xs font-medium transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteInfraction}
                  disabled={submitting}
                  className="w-full py-1.5 bg-red-600 text-white rounded-lg text-xs font-medium hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  {submitting && <FiLoader className="w-3.5 h-3.5 animate-spin" />}
                  <span>Delete</span>
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );

  // ----------------------------------------------------
  // MAIN LIST VIEW
  // ----------------------------------------------------
  return (
    <div className="space-y-4 relative">
      {toast.show &&
        ReactDOM.createPortal(
          <div
            className={`fixed top-5 right-5 z-[100000] flex items-center gap-2.5 px-4 py-3 rounded-lg shadow-xl text-white text-xs font-medium transition-all duration-200 animate-in slide-in-from-top-2 ${
              toast.type === "error" ? "bg-red-600" : "bg-emerald-600"
            }`}
          >
            {toast.type === "error" ? (
              <FiAlertCircle className="w-4 h-4 text-white shrink-0" />
            ) : (
              <FiCheckCircle className="w-4 h-4 text-white shrink-0" />
            )}
            <span className="truncate max-w-xs">{toast.message}</span>
            <button
              type="button"
              onClick={() => setToast({ show: false, message: "", type: "success" })}
              className="ml-2 text-white/80 hover:text-white transition-colors cursor-pointer"
            >
              <FiX className="w-3.5 h-3.5" />
            </button>
          </div>,
          document.body
        )}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-1">
        <div className="flex flex-wrap flex-1 items-center gap-2 max-w-2xl">
          <div className="relative flex-1 min-w-[200px]">
            <FiSearch className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              type="text"
              placeholder="Search by student, ID, or class..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-9 pr-3 py-1.5 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs text-zinc-800 focus:outline-none focus:border-zinc-400 transition-colors"
            />
            {search && (
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  setCurrentPage(1);
                }}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 cursor-pointer"
              >
                <FiX className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          <div className="relative flex-1 min-w-[150px]">
            <CustomDropdown
              label="Violation Type"
              value={selectedItem}
              options={VIOLATION_OPTIONS}
              onChange={(val) => {
                setSelectedItem(val);
                setCurrentPage(1);
              }}
              icon={FiShield}
            />
          </div>

          <div className="relative flex-1 min-w-[150px]">
            <CustomDropdown
              label="School Term"
              value={selectedTerm}
              options={termOptions}
              onChange={(val) => {
                setSelectedTerm(val);
                setCurrentPage(1);
              }}
              icon={FiClock}
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => handleOpenFormModal()}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-900 hover:bg-zinc-800 text-white text-xs font-medium rounded-lg transition-colors shadow-xs cursor-pointer"
          >
            <FiPlus className="w-3.5 h-3.5" />
            <span>Log Infraction</span>
          </button>
        </div>
      </div>

      <div className="bg-white border border-zinc-200/80 rounded-lg shadow-xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-zinc-50/80 border-b border-zinc-200/80 text-[11px] font-bold text-zinc-500 uppercase tracking-wider">
                <th className="py-3 px-4">Date & Time</th>
                <th className="py-3 px-4">Student</th>
                <th className="py-3 px-4">Classroom</th>
                <th className="py-3 px-4">Violation Item</th>
                <th className="py-3 px-4">Intervention</th>
                <th className="py-3 px-4 text-center">Weekly Total</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200/60 text-xs text-zinc-700">
              {loading ? (
                <tr>
                  <td colSpan="7" className="py-12 text-center text-zinc-400">
                    <FiRefreshCw className="w-5 h-5 animate-spin mx-auto mb-2 text-zinc-400" />
                    Loading infraction records...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="7" className="py-12 text-center text-red-500">
                    <FiAlertCircle className="w-6 h-6 mx-auto mb-2 text-red-400" />
                    {error}
                  </td>
                </tr>
              ) : infractions.length === 0 ? (
                <tr>
                  <td colSpan="7" className="py-12 text-center text-zinc-400">
                    No dress code infractions found.
                  </td>
                </tr>
              ) : (
                infractions.map((item, index) => {
                  const uniqueKey = item.id ? `infraction-${item.id}-${index}` : index;
                  const weeklyTotal = getWeeklyCount(item);

                  return (
                    <tr
                      key={uniqueKey}
                      onClick={() => handleOpenFormModal(item)}
                      className="hover:bg-zinc-50/80 transition-colors cursor-pointer group"
                    >
                      <td className="py-3 px-4 font-mono text-[11px] text-zinc-800 whitespace-nowrap">
                        {item.date_of_infraction
                          ? new Date(item.date_of_infraction).toLocaleString([], {
                              dateStyle: "short",
                              timeStyle: "short",
                            })
                          : "—"}
                      </td>

                      <td className="py-3 px-4 font-semibold text-zinc-900 group-hover:text-blue-600 transition-colors">
                        <div>{item.student_name || "—"}</div>
                        <div className="text-[10px] text-zinc-400 font-mono font-normal">
                          {item.student_uid || "No ID"}
                        </div>
                      </td>

                      <td className="py-3 px-4 text-zinc-700 font-medium">
                        {item.grade_section || "—"}
                      </td>

                      <td className="py-3 px-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200/60">
                          {item.specific_item || item.violation || "Uniform Infraction"}
                        </span>
                      </td>

                      <td className="py-3 px-4 capitalize">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-zinc-100 text-zinc-700 border border-zinc-200">
                          {item.intervention_applied_display || item.intervention_applied || "None"}
                        </span>
                      </td>

                      <td className="py-3 px-4 text-center">
                        <span className="inline-flex items-center justify-center px-2.5 py-0.5 text-xs font-bold text-blue-700">
                          {weeklyTotal}
                        </span>
                      </td>

                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={(e) => handleOpenDeleteModal(item, e)}
                            className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors cursor-pointer"
                            title="Delete Infraction"
                          >
                            <FiTrash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {!loading && !error && infractions.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalInfractions}
            pageSize={pageSize}
            onPageChange={(page) => setCurrentPage(page)}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
          />
        )}
      </div>

      {renderModals()}
    </div>
  );
}